import { useCallback, useEffect, useState } from 'react'

import { conversationApi } from './conversationApi'
import { ChatMessage, Conversation, ConversationMessage } from './types'

interface UseConversationsReturn {
  conversations: Conversation[]
  currentConversationId: string | null
  currentMessages: ChatMessage[]
  isLoading: boolean
  error: string | null
  loadConversation: (conversationId: string | null) => Promise<void>
  saveCurrentConversation: (messages: ChatMessage[]) => Promise<void>
  createNewConversation: () => void
  updateCurrentConversationId: (conversationId: string) => void
  renameConversation: (
    conversationId: string,
    newTitle: string
  ) => Promise<void>
  deleteConversation: (conversationId: string) => Promise<void>
  deleteAllConversations: () => Promise<void>
  refreshConversations: () => Promise<void>
}

export const useConversations = (
  userEmail?: string
): UseConversationsReturn => {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [currentConversationId, setCurrentConversationId] = useState<
    string | null
  >(null)
  const [currentMessages, setCurrentMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Convert API message format to ChatMessage format
  const convertApiMessageToChatMessage = (
    apiMsg: ConversationMessage
  ): ChatMessage => ({
    who: apiMsg.role === 'user' ? 'user' : 'AI',
    message: apiMsg.content,
    files: apiMsg.files,
    tools: apiMsg.tools_used
      ? { generete_chart: !!apiMsg.tools_used.generete_chart }
      : undefined,
    metadata: apiMsg.metadata,
  })

  // Convert ChatMessage format to API message format
  const convertChatMessageToApiMessage = (
    chatMsg: ChatMessage
  ): ConversationMessage => ({
    role: chatMsg.who === 'user' ? 'user' : 'assistant',
    content: chatMsg.message,
    files: chatMsg.files,
    tools_used: chatMsg.tools,
    metadata: chatMsg.metadata,
    timestamp: new Date().toISOString(),
  })

  // Convert API conversation summary to frontend Conversation format
  const convertApiConversationToConversation = (
    apiConv: any
  ): Conversation => ({
    id: apiConv.id,
    title: apiConv.title,
    lastMessage: apiConv.last_message,
    messageCount: apiConv.message_count,
    createdAt: new Date(
      apiConv.created_at + (apiConv.created_at.includes('Z') ? '' : 'Z')
    ),
    updatedAt: new Date(
      apiConv.updated_at + (apiConv.updated_at.includes('Z') ? '' : 'Z')
    ),
  })

  // Generate conversation title from first message
  const generateTitle = (messages: ChatMessage[]): string => {
    const firstUserMessage = messages.find((msg) => msg.who === 'user')
    if (firstUserMessage) {
      const title = firstUserMessage.message.slice(0, 50)
      return title.length < firstUserMessage.message.length
        ? `${title}...`
        : title
    }
    return `Nova Conversa`
  }

  // Load conversations from API
  const refreshConversations = useCallback(async () => {
    if (!userEmail) {
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      const apiConversations = await conversationApi.listConversations(50, 0)
      const convertedConversations = apiConversations.map(
        convertApiConversationToConversation
      )
      setConversations(convertedConversations)
    } catch (err) {
      console.error('Error loading conversations:', err)
      setError('Erro ao carregar conversas')
    } finally {
      setIsLoading(false)
    }
  }, [userEmail])

  // Load conversations on mount and when user changes
  useEffect(() => {
    refreshConversations()
  }, [refreshConversations])

  // Load conversation by ID
  const loadConversation = async (conversationId: string | null) => {
    setCurrentConversationId(conversationId)

    if (conversationId) {
      try {
        setIsLoading(true)
        setError(null)
        const conversationDetail = await conversationApi.getConversation(
          conversationId
        )
        const convertedMessages = conversationDetail.messages.map(
          convertApiMessageToChatMessage
        )
        setCurrentMessages(convertedMessages)
      } catch (err) {
        console.error('Error loading conversation:', err)
        setError('Erro ao carregar conversa')
        setCurrentMessages([])
      } finally {
        setIsLoading(false)
      }
    } else {
      setCurrentMessages([])
    }
  }

  // Save current conversation
  const saveCurrentConversation = async (messages: ChatMessage[]) => {
    setCurrentMessages(messages)

    // Don't save if no messages or no user
    if (messages.length === 0 || !userEmail) return

    try {
      setError(null)
      const apiMessages = messages.map(convertChatMessageToApiMessage)

      if (currentConversationId) {
        // Update existing conversation
        await conversationApi.updateConversation(currentConversationId, {
          messages: apiMessages,
        })
      } else {
        // Create new conversation
        const title = generateTitle(messages)
        const newConversation = await conversationApi.createConversation(
          title,
          apiMessages
        )
        setCurrentConversationId(newConversation.id)
      }

      // Refresh conversations list to show updated data
      await refreshConversations()
    } catch (err) {
      console.error('Error saving conversation:', err)
      setError('Erro ao salvar conversa')
    }
  }

  // Create new conversation
  const createNewConversation = () => {
    setCurrentConversationId(null)
    setCurrentMessages([])
    setError(null)
  }

  // Update current conversation ID without reloading messages
  const updateCurrentConversationId = (conversationId: string) => {
    setCurrentConversationId(conversationId)
  }

  // Rename conversation
  const renameConversation = async (
    conversationId: string,
    newTitle: string
  ) => {
    try {
      setError(null)
      await conversationApi.updateConversation(conversationId, {
        title: newTitle,
      })
      await refreshConversations()
    } catch (err) {
      console.error('Error renaming conversation:', err)
      setError('Erro ao renomear conversa')
    }
  }

  // Delete conversation
  const deleteConversation = async (conversationId: string) => {
    try {
      setError(null)
      await conversationApi.deleteConversation(conversationId)

      // If current conversation was deleted, switch to new conversation
      if (currentConversationId === conversationId) {
        createNewConversation()
      }

      await refreshConversations()
    } catch (err) {
      console.error('Error deleting conversation:', err)
      setError('Erro ao excluir conversa')
    }
  }

  // Delete all conversations
  const deleteAllConversations = async () => {
    try {
      setError(null)
      await conversationApi.deleteAllConversations()
      setConversations([])
      createNewConversation()
    } catch (err) {
      console.error('Error deleting all conversations:', err)
      setError('Erro ao excluir todas as conversas')
    }
  }

  return {
    conversations,
    currentConversationId,
    currentMessages,
    isLoading,
    error,
    loadConversation,
    saveCurrentConversation,
    createNewConversation,
    updateCurrentConversationId,
    renameConversation,
    deleteConversation,
    deleteAllConversations,
    refreshConversations,
  }
}
